import type { CallTaskId } from "@repo/app-types/entities/callTask";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  TeammateAgentBranch,
  type AdminApiPlaybookTestSpecResult,
  type AdminApiPublishedVersion,
  type AdminApiTeammateAgent,
  type TeammateAgentBranchType,
  type TeammateAgentConfigUpdatePayload,
  type TeammateAgentPatchPayload,
  type TeammateAgentPostPayload,
} from "@repo/app-types/entities/teammateAgent";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  buildListTag,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";
import { buildTeammatePerformanceGradingCacheTag } from "./performancePlaybook.js";

const teammateAgentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allTeammateAgents: builder.query<
      AdminApiTeammateAgent[],
      void,
      {
        data: {
          agents: AdminApiTeammateAgent[];
        };
      }
    >({
      query: () => "/agents",
      transformResponse: (response) => response.data.agents,
      providesTags: (result) =>
        getTagsProvidedByList(TagType.TeammateAgent, result),
    }),
    getTeammateAgent: builder.query<
      AdminApiTeammateAgent,
      | string
      | {
          /** ID of the teammate agent to fetch. */
          id: string;
          /** The branch to fetch the revision from. Defaults to the live revision. */
          branch?: TeammateAgentBranchType;
        },
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: (idOrArg) => {
        const { id, branch } =
          typeof idOrArg === "string" ? { id: idOrArg } : idOrArg;

        return {
          url: `/agents/${encodeURIComponent(id)}`,
          params: { branch },
        };
      },
      transformResponse: (response) => response.data.teammate,
      providesTags: (_result, _error, idOrArg) => [
        {
          type: TagType.TeammateAgent,
          id: typeof idOrArg === "string" ? idOrArg : idOrArg.id,
        },
      ],
    }),
    createTeammateAgent: builder.mutation<
      AdminApiTeammateAgent,
      TeammateAgentPostPayload,
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: (payload) => ({
        url: "/agents",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response) => response.data.teammate,
      invalidatesTags: [
        { type: TagType.TeammateAgent, id: "LIST" },
        buildListTag(TagType.Playbook, []),
        buildListTag(TagType.MinimalPlaybook, []),
      ],
    }),
    /**
     * Update teammate agent metadata (name only).
     * For versioned config updates (identity, tools), use updateTeammateAgentConfig instead.
     */
    updateTeammateAgent: builder.mutation<
      AdminApiTeammateAgent,
      { teammateAgentId: string; payload: TeammateAgentPatchPayload },
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: ({ teammateAgentId, payload }) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}`,
        method: "PATCH",
        body: payload,
      }),
      transformResponse: (response) => response.data.teammate,
      invalidatesTags: (_result, _error, { teammateAgentId }) => [
        { type: TagType.TeammateAgent, id: teammateAgentId },
        { type: TagType.TeammateAgent, id: "LIST" },
      ],
    }),
    /**
     * Update teammate agent config (identity and tools) with branch support.
     * Use branch=draft for autosave, branch=live for publishing.
     */
    updateTeammateAgentConfig: builder.mutation<
      AdminApiTeammateAgent,
      {
        teammateAgentId: string;
        branch?: TeammateAgentBranchType;
        payload: TeammateAgentConfigUpdatePayload;
      },
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: ({ teammateAgentId, branch, payload }) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}/config`,
        method: "PATCH",
        params: { branch },
        body: payload,
      }),
      transformResponse: (response) => response.data.teammate,
      onQueryStarted: async (
        { teammateAgentId, branch = TeammateAgentBranch.Live },
        { dispatch, queryFulfilled },
      ) => {
        const { data: updatedTeammateAgent } = await queryFulfilled;

        if (branch === TeammateAgentBranch.Live) {
          // Invalidate tags when publishing to live
          dispatch(
            teammateAgentApi.util.invalidateTags([
              { type: TagType.TeammateAgent, id: teammateAgentId },
              { type: TagType.TeammateAgent, id: "LIST" },
            ]),
          );
        } else {
          // Update cache directly for draft saves
          dispatch(
            teammateAgentApi.util.updateQueryData(
              "getTeammateAgent",
              { id: teammateAgentId, branch },
              () => updatedTeammateAgent,
            ),
          );
        }
      },
      invalidatesTags: () => [buildListTag(TagType.TeammateAgent, [])],
    }),
    /**
     * Turn outbound calling on or off. Outside the draft/publish flow on
     * purpose: this is the kill switch, so it applies immediately.
     */
    updateTeammateAgentOutboundEnabled: builder.mutation<
      AdminApiTeammateAgent,
      { teammateAgentId: string; outboundEnabled: boolean },
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: ({ teammateAgentId, outboundEnabled }) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}/outbound-enabled`,
        method: "PATCH",
        body: { outbound_enabled: outboundEnabled },
      }),
      transformResponse: (response) => response.data.teammate,
      invalidatesTags: (_result, _error, { teammateAgentId }) => [
        { type: TagType.TeammateAgent, id: teammateAgentId },
      ],
    }),
    /**
     * Cancels one of the agent's scheduled outbound calls. Resolves to whether
     * the call was stopped outright or only recorded as a request — a call
     * already being placed is not torn down, so `canceled` can be false while
     * the request still took effect for every attempt after it.
     */
    cancelTeammateAgentCallTask: builder.mutation<
      { canceled: boolean; cancelRequested: boolean },
      {
        teammateAgentId: TeammateId;
        callTaskId: CallTaskId;
        /** The interaction the call belongs to, so its detail entry refreshes. */
        gradingId: GradingId;
      },
      { data: { canceled: boolean; cancel_requested: boolean } }
    >({
      query: ({ teammateAgentId, callTaskId }) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}/call-tasks/${encodeURIComponent(callTaskId)}`,
        method: "DELETE",
      }),
      transformResponse: (response) => ({
        canceled: response.data.canceled,
        cancelRequested: response.data.cancel_requested,
      }),
      // Both surfaces move, and RTK matches tags on the exact (type, id) pair —
      // the list tag alone would leave the slideover this cancel was triggered
      // from still offering to cancel a call that is already gone.
      invalidatesTags: (_result, _error, { gradingId }) => [
        buildListTag(TagType.TeammatePerformanceGrading, []),
        buildTeammatePerformanceGradingCacheTag(gradingId),
      ],
    }),
    /**
     * Revert teammate agent config branch to live.
     */
    revertTeammateAgentConfigBranch: builder.mutation<
      AdminApiTeammateAgent,
      { teammateAgentId: string; branch: TeammateAgentBranchType },
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: ({ teammateAgentId, branch }) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}/revert`,
        method: "POST",
        params: { branch },
      }),
      transformResponse: (response) => response.data.teammate,
      onQueryStarted: async (
        { teammateAgentId, branch },
        { dispatch, queryFulfilled },
      ) => {
        const { data: revertedTeammateAgent } = await queryFulfilled;

        // Update the branch-specific query cache
        dispatch(
          teammateAgentApi.util.updateQueryData(
            "getTeammateAgent",
            { id: teammateAgentId, branch },
            () => revertedTeammateAgent,
          ),
        );
      },
      invalidatesTags: () => [buildListTag(TagType.TeammateAgent, [])],
    }),
    deleteTeammateAgent: builder.mutation<void, string>({
      query: (teammateAgentId) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, teammateAgentId) => [
        { type: TagType.TeammateAgent, id: teammateAgentId },
        { type: TagType.TeammateAgent, id: "LIST" },
        buildListTag(TagType.Playbook, []),
        buildListTag(TagType.MinimalPlaybook, []),
      ],
    }),
    createTeammateAgentTestRuns: builder.mutation<
      AdminApiPlaybookTestSpecResult[],
      { teammateAgentId: string; playbookTestSpecIds: string[] },
      {
        data: {
          playbook_test_specs_results: AdminApiPlaybookTestSpecResult[];
        };
      }
    >({
      query: ({ teammateAgentId, playbookTestSpecIds }) => ({
        url: `/agents/${encodeURIComponent(teammateAgentId)}/test-runs`,
        method: "POST",
        body: { playbook_test_spec_ids: playbookTestSpecIds },
      }),
      transformResponse: (response) =>
        response.data.playbook_test_specs_results,
      invalidatesTags: (_result, _error, { teammateAgentId }) => [
        // Invalidate the list of test spec results for this agent
        buildListTag(TagType.PlaybookTestSpecResult, [teammateAgentId]),
      ],
    }),
    getTeammateAgentTestSpecsResults: builder.query<
      AdminApiPlaybookTestSpecResult[],
      { teammateAgentId: string; playbookId: PlaybookId },
      {
        data: {
          playbook_test_specs_results: AdminApiPlaybookTestSpecResult[];
        };
      }
    >({
      query: ({ teammateAgentId, playbookId }) => {
        return {
          url: `/agents/${encodeURIComponent(teammateAgentId)}/test-specs-results`,
          params: { playbook_id: playbookId },
        };
      },
      transformResponse: (response) =>
        response.data.playbook_test_specs_results,
      providesTags: (result, _error, { teammateAgentId }) =>
        getTagsProvidedByList(TagType.PlaybookTestSpecResult, result, {
          filteredBy: [teammateAgentId],
        }),
    }),
    /**
     * Get published versions of a teammate agent config.
     * Returns all versions that have been published, ordered by most recent first.
     */
    getTeammateAgentVersions: builder.query<
      AdminApiPublishedVersion[],
      string,
      {
        data: {
          versions: AdminApiPublishedVersion[];
        };
      }
    >({
      query: (teammateAgentId) => `/agents/${teammateAgentId}/versions`,
      transformResponse: (response) => response.data.versions,
      providesTags: (_result, _error, teammateAgentId) => [
        { type: TagType.TeammateAgent, id: teammateAgentId },
      ],
    }),
    /**
     * Rollback teammate agent config to a previously published version.
     */
    rollbackTeammateAgent: builder.mutation<
      AdminApiTeammateAgent,
      { teammateAgentId: string; configRevisionId: string },
      {
        data: {
          teammate: AdminApiTeammateAgent;
        };
      }
    >({
      query: ({ teammateAgentId, configRevisionId }) => ({
        url: `/agents/${teammateAgentId}/rollback`,
        method: "POST",
        body: { config_revision_id: configRevisionId },
      }),
      transformResponse: (response) => response.data.teammate,
      invalidatesTags: (_result, _error, { teammateAgentId }) => [
        { type: TagType.TeammateAgent, id: teammateAgentId },
        buildListTag(TagType.TeammateAgent, []),
      ],
    }),
  }),
});

export const {
  useAllTeammateAgentsQuery,
  useGetTeammateAgentQuery,
  useLazyGetTeammateAgentQuery,
  useCreateTeammateAgentMutation,
  useDeleteTeammateAgentMutation,
  useUpdateTeammateAgentMutation,
  useUpdateTeammateAgentConfigMutation,
  useUpdateTeammateAgentOutboundEnabledMutation,
  useCancelTeammateAgentCallTaskMutation,
  useRevertTeammateAgentConfigBranchMutation,
  useCreateTeammateAgentTestRunsMutation,
  useGetTeammateAgentTestSpecsResultsQuery,
  useGetTeammateAgentVersionsQuery,
  useRollbackTeammateAgentMutation,
} = teammateAgentApi;
